import { CancelTokenSource } from 'axios'
import { FileManipulationKey, VisualFileType } from 'constants/visual'
import { APIRequest } from 'models/API'
import { Nullable, NullableStringIndexSignature } from 'models/helpers'
import { create } from 'zustand'

import { BaseImage, DownloadImage, UploadImage } from 'models/redux'
export type { BaseImage, DownloadImage, UploadImage }

type UploadImageStore = Nullable<NullableStringIndexSignature<NullableStringIndexSignature<UploadImage>>>
type DownloadImageStore = Nullable<NullableStringIndexSignature<NullableStringIndexSignature<DownloadImage>>>

/** Visual store shape: state[assignmentId][UPLOAD|DOWNLOAD][filename][type] */
export type VisualStoreData = NullableStringIndexSignature<{
  [FileManipulationKey.UPLOAD]?: UploadImageStore
  [FileManipulationKey.DOWNLOAD]?: DownloadImageStore
}>

interface VisualStoreState {
  visuals: VisualStoreData
  /** Tracks filenames currently being deleted, keyed by assignmentId */
  deletingFiles: Record<string, Set<string>>

  setUploadProgress: (assignmentId: string, fileName: string, type: VisualFileType, data: Partial<UploadImage>) => void
  setUploadComplete: (assignmentId: string, fileName: string, type: VisualFileType, data: Partial<UploadImage>) => void
  setUploadError: (assignmentId: string, fileName: string, type: VisualFileType, error: string) => void
  removeUpload: (assignmentId: string, fileName: string, type: VisualFileType) => void
  removeUploadWrapper: (assignmentId: string, fileName: string) => void

  setDownload: (assignmentId: string, fileName: string, type: VisualFileType, data: Partial<DownloadImage>) => void

  startDelete: (assignmentId: string, fileName: string, type: VisualFileType) => void
  completeDelete: (assignmentId: string, fileName: string, type: VisualFileType) => void
  failDelete: (assignmentId: string, fileName: string, type: VisualFileType) => void

  /** Handle replacement: set upload, clean up old file entries */
  handleUploadStart: (assignmentId: string, fileName: string, type: VisualFileType, data: UploadImage, originalFilename?: string, replaces?: string) => void
  /** Batch version of handleUploadStart — single set() call for multiple uploads */
  handleBatchUploadStart: (entries: Array<{ assignmentId: string, fileName: string, type: VisualFileType, data: UploadImage, originalFilename?: string, replaces?: string }>) => void
  handleDownloadReceived: (assignmentId: string, fileName: string, type: VisualFileType, data: DownloadImage) => void

  getUploadCancelToken: (assignmentId: string, fileName: string, type: VisualFileType) => CancelTokenSource | undefined
  getUploadStore: (assignmentId: string) => UploadImageStore | undefined
  getDownloadStore: (assignmentId: string) => DownloadImageStore | undefined
}

/** Flips the `deleting` flag on every upload/download entry of a file; the deletingFiles set is handled by the callers */
const markDeleting = (visuals: VisualStoreData, assignmentId: string, fileName: string, type: VisualFileType, deleting: boolean): VisualStoreData => {
  const assignment = visuals[assignmentId]
  if (!assignment) return visuals

  const next = { ...visuals }

  const uploadFile = assignment[FileManipulationKey.UPLOAD]?.[fileName]?.[type]
  if (uploadFile) {
    next[assignmentId] = {
      ...assignment,
      [FileManipulationKey.UPLOAD]: {
        ...assignment[FileManipulationKey.UPLOAD],
        [fileName]: {
          ...assignment[FileManipulationKey.UPLOAD]![fileName],
          [type]: { ...uploadFile, deleting },
        },
      },
    }
  }

  const downloadWrapper = assignment[FileManipulationKey.DOWNLOAD]?.[fileName]
  if (downloadWrapper) {
    const newDownloadWrapper = { ...downloadWrapper }
    for (const t of Object.keys(newDownloadWrapper)) {
      if (newDownloadWrapper[t]) {
        newDownloadWrapper[t] = { ...newDownloadWrapper[t]!, deleting }
      }
    }
    next[assignmentId] = {
      ...(next[assignmentId] || {}),
      [FileManipulationKey.DOWNLOAD]: {
        ...(next[assignmentId]?.[FileManipulationKey.DOWNLOAD] || {}),
        [fileName]: newDownloadWrapper,
      },
    }
  }

  return next
}

export const useVisualStore = create<VisualStoreState>((set, get) => ({
  visuals: {},
  deletingFiles: {},

  setUploadProgress: (assignmentId, fileName, type, data) => set(state => {
    const visuals = { ...state.visuals }
    if (!visuals[assignmentId]) visuals[assignmentId] = {}
    const assignment = { ...visuals[assignmentId]! }
    if (!assignment[FileManipulationKey.UPLOAD]) assignment[FileManipulationKey.UPLOAD] = {}
    const uploads = { ...assignment[FileManipulationKey.UPLOAD]! }
    if (!uploads[fileName]) uploads[fileName] = {}
    const fileTypes = { ...uploads[fileName]! }
    fileTypes[type] = { ...fileTypes[type]!, ...data } as UploadImage
    uploads[fileName] = fileTypes
    assignment[FileManipulationKey.UPLOAD] = uploads
    visuals[assignmentId] = assignment
    return { visuals }
  }),

  setUploadComplete: (assignmentId, fileName, type, data) => set(state => {
    const visuals = { ...state.visuals }
    if (!visuals[assignmentId]) visuals[assignmentId] = {}
    const assignment = { ...visuals[assignmentId]! }
    if (!assignment[FileManipulationKey.UPLOAD]) assignment[FileManipulationKey.UPLOAD] = {}
    const uploads = { ...assignment[FileManipulationKey.UPLOAD]! }
    if (!uploads[fileName]) uploads[fileName] = {}
    const fileTypes = { ...uploads[fileName]! }
    fileTypes[type] = { ...fileTypes[type]!, ...data, progress: 100 } as UploadImage
    uploads[fileName] = fileTypes
    assignment[FileManipulationKey.UPLOAD] = uploads
    visuals[assignmentId] = assignment
    return { visuals }
  }),

  setUploadError: (assignmentId, fileName, type, error) => set(state => {
    const visuals = { ...state.visuals }
    if (!visuals[assignmentId]) visuals[assignmentId] = {}
    const assignment = { ...visuals[assignmentId]! }
    if (!assignment[FileManipulationKey.UPLOAD]) assignment[FileManipulationKey.UPLOAD] = {}
    const uploads = { ...assignment[FileManipulationKey.UPLOAD]! }
    if (!uploads[fileName]) uploads[fileName] = {}
    const fileTypes = { ...uploads[fileName]! }
    const existing = fileTypes[type]
    if (existing) {
      fileTypes[type] = { ...existing, progress: 100, request: APIRequest.factoryError('DEFAULT_ERROR' as any, error) as any } as UploadImage
    }
    uploads[fileName] = fileTypes
    assignment[FileManipulationKey.UPLOAD] = uploads
    visuals[assignmentId] = assignment
    return { visuals }
  }),

  removeUpload: (assignmentId, fileName, type) => set(state => {
    const visuals = { ...state.visuals }
    const uploads = visuals[assignmentId]?.[FileManipulationKey.UPLOAD]
    if (uploads?.[fileName]) {
      const fileTypes = { ...uploads[fileName]! }
      delete fileTypes[type]
      uploads[fileName] = Object.keys(fileTypes).length > 0 ? fileTypes : undefined
    }
    return { visuals }
  }),

  removeUploadWrapper: (assignmentId, fileName) => {
    // Guard: skip no-op updates to avoid unnecessary re-renders
    // Match all keys with the same base filename (without extension) to handle extension mismatches
    const uploads = get().visuals[assignmentId]?.[FileManipulationKey.UPLOAD]
    if (!uploads) return
    const stripExt = (name: string) => name.replace(/\.[^.]+$/, '')
    const base = stripExt(fileName)
    const matchingKeys = Object.keys(uploads).filter(k => k === fileName || stripExt(k) === base)
    if (matchingKeys.length === 0) return

    set(state => {
      const visuals = { ...state.visuals }
      const currentUploads = visuals[assignmentId]?.[FileManipulationKey.UPLOAD]
      if (currentUploads) {
        const newUploads = { ...currentUploads }
        for (const key of matchingKeys) delete newUploads[key]
        visuals[assignmentId] = { ...visuals[assignmentId]!, [FileManipulationKey.UPLOAD]: newUploads }
      }
      return { visuals }
    })
  },

  setDownload: (assignmentId, fileName, type, data) => set(state => {
    const visuals = { ...state.visuals }
    if (!visuals[assignmentId]) visuals[assignmentId] = {}
    const assignment = { ...visuals[assignmentId]! }
    if (!assignment[FileManipulationKey.DOWNLOAD]) assignment[FileManipulationKey.DOWNLOAD] = {}
    const downloads = { ...assignment[FileManipulationKey.DOWNLOAD]! }
    if (!downloads[fileName]) downloads[fileName] = {}
    const fileTypes = { ...downloads[fileName]! }
    fileTypes[type] = { ...fileTypes[type]!, ...data, lastUpdated: Date.now() } as DownloadImage
    downloads[fileName] = fileTypes
    assignment[FileManipulationKey.DOWNLOAD] = downloads
    visuals[assignmentId] = assignment
    return { visuals }
  }),

  startDelete: (assignmentId, fileName, type) => set(state => {
    const deletingFiles = { ...state.deletingFiles }
    deletingFiles[assignmentId] = new Set([...(deletingFiles[assignmentId] ?? []), fileName])
    return { visuals: markDeleting(state.visuals, assignmentId, fileName, type, true), deletingFiles }
  }),

  failDelete: (assignmentId, fileName, type) => set(state => {
    const deletingFiles = { ...state.deletingFiles }
    const remaining = new Set(deletingFiles[assignmentId] ?? [])
    remaining.delete(fileName)
    deletingFiles[assignmentId] = remaining
    return { visuals: markDeleting(state.visuals, assignmentId, fileName, type, false), deletingFiles }
  }),

  completeDelete: (assignmentId, fileName, type) => set(state => {
    const visuals = { ...state.visuals }
    const assignment = visuals[assignmentId]
    if (!assignment) return { visuals }

    // Remove upload entries (match all keys with same base filename without extension)
    const uploads = assignment[FileManipulationKey.UPLOAD]
    if (uploads) {
      const stripExt = (name: string) => name.replace(/\.[^.]+$/, '')
      const base = stripExt(fileName)
      const matchingKeys = Object.keys(uploads).filter(k => (k === fileName || stripExt(k) === base) && uploads[k]?.[type])
      if (matchingKeys.length > 0) {
        const newUploads = { ...uploads }
        for (const uploadKey of matchingKeys) {
          const fileTypes = { ...newUploads[uploadKey]! }
          delete fileTypes[type]
          newUploads[uploadKey] = Object.keys(fileTypes).length > 0 ? fileTypes : undefined
        }
        visuals[assignmentId] = { ...assignment, [FileManipulationKey.UPLOAD]: newUploads }
      }
    }

    // Remove all download entries for this filename
    if (assignment[FileManipulationKey.DOWNLOAD]?.[fileName]) {
      const downloads = { ...assignment[FileManipulationKey.DOWNLOAD]! }
      delete downloads[fileName]
      visuals[assignmentId] = { ...(visuals[assignmentId] || {}), [FileManipulationKey.DOWNLOAD]: downloads }
    }

    // Remove from deleting tracker
    const deletingFiles = { ...state.deletingFiles }
    if (deletingFiles[assignmentId]) {
      const newSet = new Set(deletingFiles[assignmentId])
      newSet.delete(fileName)
      deletingFiles[assignmentId] = newSet
    }

    return { visuals, deletingFiles }
  }),

  handleUploadStart: (assignmentId, fileName, type, data, originalFilename, replaces) => set(state => {
    const visuals = { ...state.visuals }
    if (!visuals[assignmentId]) visuals[assignmentId] = {}
    const assignment = { ...visuals[assignmentId]! }
    if (!assignment[FileManipulationKey.UPLOAD]) assignment[FileManipulationKey.UPLOAD] = {}
    const uploads = { ...assignment[FileManipulationKey.UPLOAD]! }
    if (!uploads[fileName]) uploads[fileName] = {}
    const fileTypes = { ...uploads[fileName]! }
    fileTypes[type] = data
    uploads[fileName] = fileTypes

    // Delete originalFilename upload entry (when server renames the file)
    if (originalFilename && originalFilename !== fileName && uploads[originalFilename]?.[type]) {
      const origFileTypes = { ...uploads[originalFilename]! }
      delete origFileTypes[type]
      if (Object.keys(origFileTypes).length === 0) {
        delete uploads[originalFilename]
      } else {
        uploads[originalFilename] = origFileTypes
      }
    }

    assignment[FileManipulationKey.UPLOAD] = uploads

    // Delete download entry if replacing
    if (replaces && assignment[FileManipulationKey.DOWNLOAD]?.[replaces]) {
      const downloads = { ...assignment[FileManipulationKey.DOWNLOAD]! }
      delete downloads[replaces]
      assignment[FileManipulationKey.DOWNLOAD] = downloads
    }

    visuals[assignmentId] = assignment
    return { visuals }
  }),

  handleBatchUploadStart: (entries) => set(state => {
    const visuals = { ...state.visuals }
    for (const { assignmentId, fileName, type, data, originalFilename, replaces } of entries) {
      if (!visuals[assignmentId]) visuals[assignmentId] = {}
      const assignment = { ...visuals[assignmentId]! }
      if (!assignment[FileManipulationKey.UPLOAD]) assignment[FileManipulationKey.UPLOAD] = {}
      const uploads = { ...assignment[FileManipulationKey.UPLOAD]! }
      if (!uploads[fileName]) uploads[fileName] = {}
      const fileTypes = { ...uploads[fileName]! }
      fileTypes[type] = data
      uploads[fileName] = fileTypes

      if (originalFilename && originalFilename !== fileName && uploads[originalFilename]?.[type]) {
        const origFileTypes = { ...uploads[originalFilename]! }
        delete origFileTypes[type]
        if (Object.keys(origFileTypes).length === 0) {
          delete uploads[originalFilename]
        } else {
          uploads[originalFilename] = origFileTypes
        }
      }

      assignment[FileManipulationKey.UPLOAD] = uploads

      if (replaces && assignment[FileManipulationKey.DOWNLOAD]?.[replaces]) {
        const downloads = { ...assignment[FileManipulationKey.DOWNLOAD]! }
        delete downloads[replaces]
        assignment[FileManipulationKey.DOWNLOAD] = downloads
      }

      visuals[assignmentId] = assignment
    }
    return { visuals }
  }),

  handleDownloadReceived: (assignmentId, fileName, type, data) => set(state => {
    const visuals = { ...state.visuals }
    if (!visuals[assignmentId]) visuals[assignmentId] = {}
    const assignment = { ...visuals[assignmentId]! }
    if (!assignment[FileManipulationKey.DOWNLOAD]) assignment[FileManipulationKey.DOWNLOAD] = {}
    const downloads = { ...assignment[FileManipulationKey.DOWNLOAD]! }
    if (!downloads[fileName]) downloads[fileName] = {}
    const fileTypes = { ...downloads[fileName]! }
    fileTypes[type] = data
    downloads[fileName] = fileTypes
    assignment[FileManipulationKey.DOWNLOAD] = downloads

    // Upload cleanup is handled by GalleryVisuals.context when FileAPI takes over display

    visuals[assignmentId] = assignment
    return { visuals }
  }),

  getUploadCancelToken: (assignmentId, fileName, type) => {
    return get().visuals[assignmentId]?.[FileManipulationKey.UPLOAD]?.[fileName]?.[type]?.cancelToken
  },

  getUploadStore: (assignmentId) => {
    return get().visuals[assignmentId]?.[FileManipulationKey.UPLOAD]
  },

  getDownloadStore: (assignmentId) => {
    return get().visuals[assignmentId]?.[FileManipulationKey.DOWNLOAD]
  },
}))
